import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { Link as ScrollLink } from 'react-scroll';

import env from 'Env';
import Drm from 'Components/Utility/Drm';
import StarRating from 'Components/ProductReviews/UserReviews/StarRating';
import StarRatingToolTip from 'Components/ProductReviews/UserReviews/StarRatingToolTip';
import store from 'Redux/store';
import useViewport from 'Hooks/useViewport';
import { IsChallenger } from 'Redux/ducks/split-test';

import 'Components/Product/ReviewHeaderSection.scss';

export default function ReviewHeaderSection({ product, theme, showToolTip }) {
  const { isAboveMd } = useViewport();
  const recommendedThreshold = env.percentRecommendedThreshold;
  const steamThreshold = env.steamPercentThreshold;

  const userReviewRatingScore = get(product.userReviewSummary, 'rating_score');
  const userReviewTotalRatings = get(product.userReviewSummary, 'total_ratings');

  const reduxStore = store.getState();
  const componetTheme = theme || reduxStore.siteTheme;

  let reviewScore = 0;
  let enoughCriticReviews = false;

  let reviewImgLink = `https://cdn.fanatical.com/production/brand/metacritic/svg/metacritic-full-${componetTheme === 'light' ? 'black' : 'white'}.svg`;
  let reviewImgAlt = 'Metacritic';

  if (product.metaCritic && get(product, 'metaCritic.score') >= recommendedThreshold) {
    reviewScore = get(product, 'metaCritic.score', 0);
    enoughCriticReviews = get(product, 'metaCritic.reviews.length', 0) >= 3;
  } else if (product.openCritic && get(product, 'openCritic.score') >= recommendedThreshold) {
    reviewImgLink = `https://cdn.fanatical.com/production/brand/opencritic/svg/opencritic-full-${componetTheme === 'light' ? 'black' : 'white'}.svg`;
    reviewImgAlt = 'OpenCritic';

    reviewScore = get(product, 'openCritic.score', 0);
    enoughCriticReviews = get(product, 'openCritic.reviews.length', 0) >= 3;
  }

  const showCriticScore = reviewScore >= recommendedThreshold;

  // Steam Reviews Logic
  let steamReviewScore = 0;

  const steamReviewSummary = product?.steamReviewSummary;
  const steamReviewScoreDesc = steamReviewSummary?.review_score_desc;

  if (steamReviewSummary) {
    steamReviewScore = Math.round(
      (steamReviewSummary.total_positive / steamReviewSummary.total_reviews) * 100,
    );
  }

  const showSteamReviewScore = steamReviewScore >= steamThreshold && product?.drm?.steam && isAboveMd && IsChallenger('ProductSteamReviewsV2');

  if (product.preorder && !showCriticScore && !showSteamReviewScore) {
    return false;
  }

  if (!showCriticScore && !userReviewRatingScore && !showSteamReviewScore) {
    return false;
  }

  return (
    <div className="product-stars-critic-reviews-container">
      {!product.preorder && showToolTip &&
        <StarRatingToolTip
          userReviewSummary={product.userReviewSummary}
          slug={product.slug}
          type={product.type}
          showToolTip={showToolTip}
        />
      }

      {!showToolTip && userReviewRatingScore &&
        <React.Fragment>
          <StarRating rating={userReviewRatingScore} />
          <div className="user-review-score-no-tooltip">({userReviewTotalRatings})</div>
        </React.Fragment>
      }

      {showCriticScore && enoughCriticReviews && window.locale === 'en' &&
        <div className="critic-review-rating-container d-flex align-items-center">
          <ScrollLink
            spy
            smooth
            activeClass="active"
            to="critic-reviews"
            duration={500}
            offset={-80}
            className="critic-review-rating-link"
          >
            <div className="open-critic-score d-flex align-items-center justify-content-center">
              {reviewScore}
            </div>
            <div className="review-text-container">
              <img
                src={reviewImgLink}
                width="100%"
                height="100%"
                alt={reviewImgAlt}
              />
            </div>
          </ScrollLink>
        </div>
      }

      {showCriticScore && (!enoughCriticReviews || window.locale !== 'en') &&
        <div className="critic-review-rating-container d-flex align-items-center">
          <div className="open-critic-score d-flex align-items-center justify-content-center">
            {reviewScore}
          </div>
          <div className="review-text-container">
            <img
              src={reviewImgLink}
              width="100%"
              height="100%"
              alt={reviewImgAlt}
            />
          </div>
        </div>
      }

      {showSteamReviewScore &&
        <div className="critic-review-steam">
          {product?.drm?.steam &&
            <Drm drmType={'steam'} colorOverride="light" iconSize={'small'} />
          }
          <div>
            {steamReviewScore}% | {steamReviewScoreDesc}
          </div>
        </div>
      }
    </div>
  );
}

ReviewHeaderSection.propTypes = {
  product: PropTypes.shape({}).isRequired,
  theme: PropTypes.string,
  showToolTip: PropTypes.bool,
};

ReviewHeaderSection.defaultProps = {
  theme: '',
  showToolTip: true,
};
